import { PAGE_PARAMETER, VQL_PARAMETER } from "helpers/constants/miscellaneous";
import { setShowQueryPanel } from "redux/SingleDataset/actions";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import {
  APIQueryOperators,
  FilterKey,
  FilterSubType,
  Filters,
  FilterType,
  Query,
  QueryOperator,
  QueryPanelLogic,
  VqlQuery,
  VqlQueryBase,
  isFilterSubType,
} from "types";
import { isVqlQuery } from "helpers/utility/vqlHelpers";
import { getTags } from "redux/SingleDataset/selectors";

interface VqlParamsHook {
  vqlQueries: VqlQuery[];
  findVqlQuery: <K extends FilterKey>(
    id: string,
    key: K
  ) => (VqlQueryBase & Extract<FilterType, Record<K, any>>) | null;
  setVqlQueries: (queries: VqlQuery[]) => void;
  addVqlQuery: (query: VqlQuery) => void;
  removeVqlQuery: (queryId: string) => void;
  updateVqlQuery: (newQuery: VqlQuery) => void;
  vqlToQuery: (vqlQuery: VqlQuery) => Query | null;
}

const useVqlParams = (): VqlParamsHook => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userTags = useSelector((state) => getTags(state));

  const getVqlQueries = useCallback((): VqlQuery[] => {
    const searchParams = new URLSearchParams(location.search);
    const vqlParam = searchParams.get(VQL_PARAMETER);
    if (!vqlParam) return [];
    try {
      return JSON.parse(vqlParam) as VqlQuery[];
    } catch {
      return [];
    }
  }, [location.search]);

  const vqlQueries = getVqlQueries();

  const setVqlQueries = (queries: VqlQuery[]) => {
    const newSearchParams = new URLSearchParams(location.search);
    if (queries.length === 0) {
      newSearchParams.delete(VQL_PARAMETER);
    } else {
      newSearchParams.set(PAGE_PARAMETER, "1");
      newSearchParams.set(VQL_PARAMETER, JSON.stringify(queries));
    }
    navigate({ search: newSearchParams.toString() });
  };

  const mixMatchingQuery = (
    existingQuery: VqlQuery,
    newQuery: VqlQuery
  ): VqlQuery | null => {
    if (
      isVqlQuery(existingQuery, "labels") &&
      isVqlQuery(newQuery, "labels") &&
      existingQuery.labels.op === newQuery.labels.op
    ) {
      const resultQuery = { ...existingQuery };
      resultQuery.labels.value = Array.from(
        new Set([...existingQuery.labels.value, ...newQuery.labels.value])
      );
      return resultQuery;
    }

    if (
      isVqlQuery(existingQuery, "issues") &&
      isVqlQuery(newQuery, "issues") &&
      existingQuery.issues.value === newQuery.issues.value &&
      existingQuery.issues.mode === newQuery.issues.mode
    ) {
      return newQuery;
    }

    if (
      isVqlQuery(existingQuery, "tags") &&
      isVqlQuery(newQuery, "tags") &&
      existingQuery.tags.op === newQuery.tags.op
    ) {
      const resultQuery = { ...existingQuery };
      resultQuery.tags.value = Array.from(
        new Set([...existingQuery.tags.value, ...newQuery.tags.value])
      );
      return resultQuery;
    }

    if (
      isVqlQuery(existingQuery, "text") &&
      isVqlQuery(newQuery, "text") &&
      existingQuery.text.op === newQuery.text.op
    ) {
      return newQuery;
    }

    if (
      isVqlQuery(existingQuery, "duplicates") &&
      isVqlQuery(newQuery, "duplicates") &&
      existingQuery.duplicates.op === newQuery.duplicates.op
    ) {
      return newQuery;
    }

    if (
      isVqlQuery(existingQuery, "uniqueness") &&
      isVqlQuery(newQuery, "uniqueness")
    ) {
      return newQuery;
    }
    if (
      isVqlQuery(existingQuery, "file") &&
      isVqlQuery(newQuery, "file") &&
      existingQuery.file.op === newQuery.file.op
    ) {
      const resultQuery = { ...existingQuery };
      const existingValues = Array.isArray(existingQuery.file.value)
        ? existingQuery.file.value
        : [existingQuery.file.value];
      const newValues = Array.isArray(newQuery.file.value)
        ? newQuery.file.value
        : [newQuery.file.value];

      resultQuery.file.value = Array.from(
        new Set([...existingValues, ...newValues])
      );
      return resultQuery;
    }

    if (
      isVqlQuery(existingQuery, "folder") &&
      isVqlQuery(newQuery, "folder") &&
      existingQuery.folder.op === newQuery.folder.op
    ) {
      const resultQuery = { ...existingQuery };
      const existingValues = Array.isArray(existingQuery.folder.value)
        ? existingQuery.folder.value
        : [existingQuery.folder.value];
      const newValues = Array.isArray(newQuery.folder.value)
        ? newQuery.folder.value
        : [newQuery.folder.value];

      resultQuery.folder.value = Array.from(
        new Set([...existingValues, ...newValues])
      );
      return resultQuery;
    }
    return null;
  };

  const addVqlQuery = (query: VqlQuery) => {
    const allQueries = getVqlQueries();
    let mixedQuery: VqlQuery | null = null;
    let existingQueryIndex: number = -1;
    for (let i = 0; i < allQueries.length; i++) {
      mixedQuery = mixMatchingQuery(allQueries[i], query);
      if (mixedQuery !== null) {
        existingQueryIndex = i;
        break;
      }
    }
    if (existingQueryIndex !== -1 && mixedQuery !== null) {
      const updatedQueries = [...allQueries];
      updatedQueries[existingQueryIndex] = mixedQuery;
      setVqlQueries(updatedQueries);
    } else {
      setVqlQueries([...allQueries, query]);
    }
    dispatch(setShowQueryPanel(true));
  };

  const removeVqlQuery = (queryId: string) => {
    const currentQueries = getVqlQueries();
    setVqlQueries(
      currentQueries.filter((query: VqlQuery) => query.id !== queryId)
    );
  };

  const updateVqlQuery = (newQuery: VqlQuery) => {
    const currentQueries = getVqlQueries();
    setVqlQueries(
      currentQueries.map((query: VqlQuery) =>
        query.id === newQuery.id ? newQuery : query
      )
    );
  };

  const findVqlQuery = <K extends FilterKey>(
    id: string,
    key: K
  ): (VqlQueryBase & Extract<FilterType, Record<K, any>>) | null => {
    const queries = getVqlQueries();
    const matchingQuery = queries.find((query) => query.id === id);

    // If the query is found, assert the type
    if (matchingQuery && isVqlQuery(matchingQuery, key)) {
      return matchingQuery as VqlQueryBase &
        Extract<FilterType, Record<K, any>>;
    }

    // Return null if not found or the key doesn't match
    return null;
  };

  const vqlToQuery = (vqlQuery: VqlQuery): Query | null => {
    const mapOperator = (op: APIQueryOperators): QueryOperator => {
      const operatorMap: Record<APIQueryOperators, QueryOperator> = {
        one_of: QueryOperator.IS_ONE_OF,
        not_one_of: QueryOperator.IS_NOT_ONE_OF,
        is: QueryOperator.IS,
        is_not: QueryOperator.IS_NOT,
        contains: QueryOperator.CONTAINS,
        starts_with: QueryOperator.STARTS_WITH,
        ends_with: QueryOperator.ENDS_WITH,
        fts: QueryOperator.IS,
        semantic: QueryOperator.IS,
        media: QueryOperator.IS,
        upload: QueryOperator.IS,
        issues: QueryOperator.IS,
        in: QueryOperator.IS,
        out: QueryOperator.IS_NOT,
        duplicates: QueryOperator.IS,
        uniqueness: QueryOperator.IS,
        eq: QueryOperator.IS,
        neq: QueryOperator.IS_NOT,
        gt: QueryOperator.GREATER_THAN,
        lt: QueryOperator.LESS_THAN,
        gte: QueryOperator.GREATER_THAN_OR_EQUAL_TO,
        lte: QueryOperator.LESS_THAN_OR_EQUAL_TO,
        between: QueryOperator.BETWEEN,
        does_not_contain: QueryOperator.DOES_NOT_CONTAIN,
      };
      return operatorMap[op];
    };

    if ("labels" in vqlQuery && vqlQuery.labels) {
      return {
        id: vqlQuery.id,
        type: Filters.LABELS,
        operator: mapOperator(vqlQuery.labels.op),
        values: vqlQuery.labels.value,
        logic: QueryPanelLogic.AND,
      };
    }

    if ("issues" in vqlQuery && vqlQuery.issues) {
      const separatedIssues = ["mislabels", "outliers", "label_outlier"];
      const subType = vqlQuery.issues.value.toUpperCase() as FilterSubType;
      const showsSeparately = separatedIssues.includes(vqlQuery.issues.value);

      const type =
        separatedIssues[0] === vqlQuery.issues.value
          ? Filters.MISLABELS
          : separatedIssues[1] === vqlQuery.issues.value
          ? Filters.OUTLIERS
          : separatedIssues[2] === vqlQuery.issues.value
          ? Filters.LABEL_OUTLIER
          : Filters.ISSUES;

      return {
        type,
        id: vqlQuery.id,
        operator: mapOperator(vqlQuery.issues.mode),
        values:
          isFilterSubType(subType) || showsSeparately
            ? [
                vqlQuery.issues.confidence_min.toString(),
                vqlQuery.issues.confidence_max.toString(),
              ]
            : [vqlQuery.issues.value],
        logic: QueryPanelLogic.AND,
        subType: isFilterSubType(subType) ? subType : undefined,
      };
    }

    if ("tags" in vqlQuery && vqlQuery.tags) {
      return {
        id: vqlQuery.id,
        type: Filters.USER_TAGS,
        operator: mapOperator(vqlQuery.tags.op),
        values: userTags
          .filter((tag) => vqlQuery.tags.value.includes(tag.id.toString()))
          .map((tag) => tag.text),
        logic: QueryPanelLogic.AND,
      };
    }

    if ("file" in vqlQuery && vqlQuery.file) {
      return {
        id: vqlQuery.id,
        type: Filters.FILE,
        operator: mapOperator(vqlQuery.file.op),
        values: Array.isArray(vqlQuery.file.value)
          ? vqlQuery.file.value
          : [vqlQuery.file.value],
        logic: QueryPanelLogic.AND,
      };
    }

    if ("folder" in vqlQuery && vqlQuery.folder) {
      return {
        id: vqlQuery.id,
        type: Filters.FOLDER,
        operator: mapOperator(vqlQuery.folder.op),
        values: Array.isArray(vqlQuery.folder.value)
          ? vqlQuery.folder.value
          : [vqlQuery.folder.value],
        logic: QueryPanelLogic.AND,
      };
    }

    if ("text" in vqlQuery && vqlQuery.text && vqlQuery.text.op === "fts") {
      return {
        id: vqlQuery.id,
        type: Filters.CAPTION,
        operator: mapOperator(vqlQuery.text.op),
        values: [vqlQuery.text.value],
        logic: QueryPanelLogic.AND,
      };
    }

    if ("duplicates" in vqlQuery && vqlQuery.duplicates) {
      return {
        id: vqlQuery.id,
        type: Filters.DUPLICATES,
        operator: mapOperator(vqlQuery.duplicates.op),
        values: [vqlQuery.duplicates.value.toString()],
        logic: QueryPanelLogic.AND,
      };
    }

    if ("uniqueness" in vqlQuery && vqlQuery.uniqueness) {
      return {
        id: vqlQuery.id,
        type: Filters.UNIQUENESS,
        operator: mapOperator(vqlQuery.uniqueness.op),
        values: [vqlQuery.uniqueness.value.toString()],
        logic: QueryPanelLogic.AND,
      };
    }

    if ("custom_metadata" in vqlQuery && vqlQuery.custom_metadata) {
      const formatValue = (value: any): string[] => {
        if (Array.isArray(value)) {
          return value.map(String);
        }
        return [String(value)];
      };

      return {
        id: vqlQuery.id,
        type: vqlQuery.custom_metadata,
        operator: mapOperator(vqlQuery.custom_metadata.op),
        values: formatValue(vqlQuery.custom_metadata.value),
        logic: QueryPanelLogic.AND,
      };
    }

    if ("size" in vqlQuery && vqlQuery.size) {
      return {
        id: vqlQuery.id,
        type: Filters.SIZE,
        operator: mapOperator(vqlQuery.size.op),
        values: [vqlQuery.size.value.toString()],
        logic: QueryPanelLogic.AND,
      };
    }


    return null;
  };

  return {
    vqlQueries,
    findVqlQuery,
    setVqlQueries,
    addVqlQuery,
    removeVqlQuery,
    updateVqlQuery,
    vqlToQuery,
  };
};

export default useVqlParams;
